/* eslint-disable no-nested-ternary */
/* eslint-disable consistent-return */
/* eslint-disable max-len */

import { FC, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import isMatch from 'lodash/isMatch';
import {
  Badge,
  Button,
  Col,
  Collapse,
  Form,
  InputNumber,
  Drawer,
  Modal,
  Row,
  Select,
  Skeleton,
  Switch,
  Tabs,
  Typography,
  Spin,
  Tooltip,
  Input,
  message,
  Tag,
  Dropdown,
  Menu,
  Popconfirm,
} from 'antd';
import { debounce } from 'lodash';
import { Link } from 'react-router-dom';
import { RootState } from 'store';
import {
  DoubleLeftOutlined,
  DoubleRightOutlined,
  ExclamationCircleFilled,
  InfoCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import useStickyState from 'utils/useStickyState';
import { TbCalendarEvent, TbClock, TbClockPlus, TbCurrencyDollar, TbStatusChange } from 'react-icons/tb';
import { MdKeyboardDoubleArrowDown, MdKeyboardDoubleArrowUp } from 'react-icons/md';
import { taskTimelogReportSlice } from 'modules/tasks/store/taskTimelogReport';
import getTaskStatusOptions, { getTaskStatusOption } from 'modules/project/config/getTaskStatusOptions';
import RichTextEditor from 'components/RichTextEditor';
import UserAvatarsPopover from 'components/UserAvatarsPopover';
import UploadDragger from 'components/FileUpload/UploadDragger';
import StatusTag from 'components/StatusTag';
import BillableToggle from 'components/BillableToggle';
import Comments from 'components/Comments';
import TimerDropdown from 'components/Timer/TimerDropdown';
import ErrorResult from 'components/ErrorResult';
import EditableParagraphInput, { EditableParagraphInputHandle } from 'components/EditableParagraphInput';
import FieldTimeRangeCustom from 'components/FieldTimeRangeCustom';
import ActivityLogs from 'components/ActivityLogs';
import { convertSnakeCaseToTitleCase, getNameAbbrev } from 'utils/string.utils';
import apiRequests from 'utils/api';
import apiRoutes from 'config/apiRoute';
import type { UploadFileApi } from 'types/upload-type';
import { FormBuilderData, FormBuilderFormData } from 'types/formBuilder';
import { FaCheckCircle } from 'react-icons/fa';
import { BiSolidError } from 'react-icons/bi';
import useWebSocket from 'utils/useWebSocket';
import { TIME_ENTRY_WS_EVENT, isLocalEcho, TimeEntryWsEvent } from 'services/timeTracking';
import Checklist from 'components/Checklist';
import { TaskUpdateEvent } from 'components/TasksKanban';
import { HiDotsVertical } from 'react-icons/hi';
import ProjectTag from './components/ProjectTag';
import ParentTask from './components/ParentTask';
import PriorityDropdown from './components/PriorityDropdown';
import SubTasksCollapse from './components/SubTasksCollapse';
import TaskTagSelect from './components/TaskTagSelect';
import SelectFollowers from './components/SelectFollowers';
import RequestCard from './components/RequestCard';
import SelectResponsible from './components/SelectResponsible';
import StartDueDatePicker from './components/StartDueDatePicker';
import useTaskModal from './useTaskModal';
import TaskTimelogTable from './components/TaskTimelogTable';
import './index.styles.less';

const { Panel } = Collapse;
const { Text } = Typography;

const descriptionPlaceholder = 'Describe in detail what your requirements and needs are.';

const FALLBACK_INTERVAL_MS = 8000;

const debouncedUpdate = debounce(async (values: Record<string, any>, onUpdateHandler: any) => {
  return onUpdateHandler(values);
}, 600);

interface TaskModalBodyHandle {
  safeClose: (force?: boolean) => void;
}

interface TaskModalBodyProps {
  uuid: any;
  highlightCommentUuid?: string;
  createToParentUuid?: string;
  createToStatus?: string;
  createFromTaskUuid?: string;
  project?: any;
  initialFiles?: UploadFileApi[];
  isPersonal?: boolean;
  hideComments?: boolean;
  onClose?: () => void;
  onCommentsLoaded?: any;
  onAddTimeLogEntry?: any;
  onRemoveRequest?: (taskId: string) => void;
  onUpdateSubtask?: () => void;
  onDeleteTask?: () => void;
  onImageModalChanged?: (open: boolean) => void;
  onCustomizeDataApi?: (values: Record<string, any>) => Record<string, any>;
  onSaved?: (data: Record<string, any>) => void;
}

const getChecklistStatus = (
  checklistJson: any,
): {
  status: 'completed' | 'pending' | 'error';
  icon: React.ReactNode;
  total: number;
  completed: number;
  statusLocks: string[];
} | null => {
  if (!checklistJson) {
    return null;
  }

  try {
    const checklistData = JSON.parse(checklistJson);
    let totalItems = 0;
    let completedItems = 0;
    const statusLocksSet = new Set<string>();

    const checkFields = (fields: FormBuilderFormData[]) => {
      fields.forEach((item) => {
        if ('name' in item && 'options' in item && Array.isArray(item.options)) {
          totalItems++;

          const optionsCount = item.options?.length ?? 0;
          const answersCount = Array.isArray(item.answer) ? item.answer.length : 0;

          if (optionsCount > 0 && optionsCount === answersCount) {
            completedItems++;
          }

          if ('statusLock' in item && item.statusLock) {
            const isIncomplete = optionsCount > 0 && answersCount > 0 && answersCount < optionsCount;

            if (isIncomplete) {
              const locks = String(item.statusLock)
                .split(',')
                .map((lock) => lock.trim())
                .filter(Boolean);

              locks.forEach((lock) => statusLocksSet.add(lock));
            }
          }
        }
      });
    };

    if (checklistData.form) {
      checkFields(checklistData.form);
    }

    const resultBase = {
      total: totalItems,
      completed: completedItems,
      statusLocks: Array.from(statusLocksSet),
    };

    if (completedItems === totalItems) {
      return {
        ...resultBase,
        status: 'completed',
        icon: <FaCheckCircle color="green" size={14} />,
      };
    }

    return {
      ...resultBase,
      status: 'pending',
      icon: (
        <div style={{ position: 'relative', overflow: 'hidden', borderRadius: '50%', width: 14, height: 14 }}>
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: 14,
              height: 14,
              backgroundColor: 'red',
              padding: 2,
              zIndex: 1,
              borderRadius: '50%',
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              left: '50%',
              width: 8,
              height: 8,
              backgroundColor: 'white',
              padding: 2,
              zIndex: 2,
              borderRadius: '50%',
            }}
          />
        </div>
      ),
    };
  } catch (e) {
    console.error('Failed to analyze checklist status:', e);
    return {
      status: 'error',
      total: 0,
      completed: 0,
      icon: <BiSolidError color="red" size={14} />,
      statusLocks: [],
    };
  }
};

const hasAnyAnswer = (checklistJson: string | null) => {
  if (!checklistJson) return false;

  try {
    const data = JSON.parse(checklistJson);
    if (!data.form) return false;

    return data.form.some((item: any) => {
      return Array.isArray(item.answer) && item.answer.length > 0;
    });
  } catch {
    return false;
  }
};

const hasNoTimelogs = async (taskUuid: string) => {
  const response = await apiRequests.get(`${apiRoutes.TIME_ENTRIES}`, {
    'filter[task]': taskUuid,
    'page[size]': 1,
  });
  return response.data.data.length === 0;
};

const TaskModalBody = forwardRef<TaskModalBodyHandle, TaskModalBodyProps>(
  (
    {
      uuid,
      highlightCommentUuid,
      createToParentUuid,
      createToStatus,
      createFromTaskUuid,
      project,
      initialFiles,
      isPersonal,
      hideComments,
      onClose,
      onCommentsLoaded,
      onAddTimeLogEntry,
      onRemoveRequest,
      onUpdateSubtask,
      onImageModalChanged,
      onDeleteTask,
      onSaved,
      onCustomizeDataApi,
    },
    ref,
  ) => {
    const {
      state,
      setData,
      initialValues,
      form,
      isCreateModal,
      filesData,
      fetchTaskError,
      scrollRef,
      setState,
      createToParentTask,
      selectedProject,
      updateDirtyFields,
      onCreateHandler,
      onUpdateHandler,
      onUpdateParentTask,
      onButtonUpdateHandler,
      onFieldsChangeHandler,
      setProject,
      setFilesData,
      fetchTask,
      scrollToBottom,
      fetching,
      fetchingParent,
      saving,
      connectedUsers,
      setConnectedUsers,
      editorStatus,
      setEditorStatus,
      descriptionTextRef,
    } = useTaskModal({
      uuid,
      project,
      initialFiles,
      isPersonal,
      createToParentUuid,
      createToStatus,
      createFromTaskUuid,
      onSaved,
      onCustomizeDataApi,
    });

    const listChecklistTemplates = useSelector((state: RootState) => state.resources.checklists) ?? [];

    const [hasFileLoading, setHasFileLoading] = useState(false);
    const [loadingImages, setLoadingImages] = useState(0);
    const [openUploadingFiles, setOpenUploadingFiles] = useState(false);
    const [openUnsavedAlert, setOpenUnsavedAlert] = useState(false);

    const [isPollingActive, setIsPollingActive] = useState(false);

    const [collapsedFilters, setCollapsedFilters] = useState(isCreateModal);
    const [selectedChecklist, setSelectedChecklist] = useState<FormBuilderData>();

    const [checklistStatus, setChecklistStatus] = useState(getChecklistStatus(initialValues?.checklist ?? null));
    const [noTimelogs, setNoTimelogs] = useState<boolean | null>(null);
    const hasCheckListAnswers = hasAnyAnswer(state?.checklist ?? null);

    const user = useSelector((store: any) => store.auth.user);

    const assigneeUuid = state?.related_users?.find((user: any) => user.pivot.type === 'responsible')?.uuid;
    const isAssigneeEditingTheirOwnTask = assigneeUuid === user.uuid;

    const titleRef = useRef<EditableParagraphInputHandle>(null);
    const dispatch = useDispatch();

    const handleChecklistUpdate = async (checklistValue: FormBuilderData) => {
      const checklistValueJson = JSON.stringify(checklistValue);

      setState({ ...state, checklist: checklistValueJson });
      form.setFieldsValue({ checklist: checklistValueJson });

      if (isCreateModal) {
        return {};
      }

      const originalChecklistJson = form.getFieldValue('checklist');

      try {
        await debouncedUpdate({ checklist: checklistValueJson }, onUpdateHandler);
        return {};
      } catch (error) {
        console.error('Error saving checklist. Reverting...', error);

        form.setFieldsValue({ checklist: originalChecklistJson });
        setChecklistStatus(getChecklistStatus(originalChecklistJson));
        return {};
      }
    };

    const checklistStatusRender = checklistStatus ? (
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <span style={{ lineHeight: 1.5 }}>Checklist</span>
        {checklistStatus.icon}
      </div>
    ) : (
      <span>Checklist</span>
    );

    const fetchNoTimelogs = async (uuid: any) => {
      try {
        const result = await hasNoTimelogs(uuid);
        setNoTimelogs(result);
      } catch {
        setNoTimelogs(false);
      }
    };

    const handleBillableChange = async (nextBillable: boolean) => {
      const response = await onUpdateHandler({ billable: nextBillable });

      if (response) {
        setData(response.data.data);
      }
    };

    const handleDeleteTask = async () => {
      try {
        await apiRequests.delete(`${apiRoutes.PROJECT_TASKS}/${uuid}`);

        onDeleteTask?.();
        onClose?.();
      } catch (newError: any) {
        if (newError?.response?.status === 422) {
          const errors = newError?.response?.data?.errors;
          const keys = Object.keys(errors ?? {});
          const values = keys.map((key) => errors[key]);
          message.error(values[0]);
        } else {
          message.error(newError?.message ?? 'Something went wrong!');
        }
      }
    };

    useImperativeHandle(ref, () => ({
      safeClose(force = false) {
        if (force) {
          onClose?.();
          return;
        }

        if (!isCreateModal && (hasFileLoading || loadingImages)) {
          setOpenUploadingFiles(true);
          setIsPollingActive(true);
          return;
        }

        const currentValues = form.getFieldsValue();
        const hasTitle = !!currentValues.title;
        const hasProjectId = !!currentValues.project_id;

        if (isCreateModal && hasTitle && hasProjectId) {
          setOpenUnsavedAlert(true);
          return;
        }

        if (state?.recurrence && !isCreateModal && !isMatch(initialValues, form.getFieldsValue())) {
          setOpenUnsavedAlert(true);
          return;
        }

        if (!isCreateModal) {
          updateDirtyFields();
        }

        onClose?.();
      },
    }));

    useEffect(() => {
      if (openUploadingFiles && !hasFileLoading && !loadingImages) {
        setOpenUploadingFiles(false);
        setIsPollingActive(false);
        onClose?.();
        return;
      }

      if (isPollingActive && openUploadingFiles) {
        const intervalId = setInterval(() => {}, FALLBACK_INTERVAL_MS);

        return () => clearInterval(intervalId);
      }

      return () => {};
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openUploadingFiles, hasFileLoading, loadingImages, isPollingActive, onClose]);

    useEffect(() => {
      if (isCreateModal && project) {
        const timeout = setTimeout(() => titleRef.current?.focus(), 0);

        return () => {
          clearTimeout(timeout);
        };
      }

      return () => {};
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (!uuid) return;

      fetchNoTimelogs(uuid);
    }, [uuid]);

    useEffect(() => {
      if (state && state.checklist) {
        const checklistData = JSON.parse(state.checklist);

        setSelectedChecklist(checklistData);
        setChecklistStatus(getChecklistStatus(state.checklist));
      }
    }, [state]);

    useWebSocket({
      channelName: 'model.changes',
      listen: {
        event: TIME_ENTRY_WS_EVENT,
        callback: (event: TimeEntryWsEvent) => {
          if (isLocalEcho(event)) return;

          if (uuid && event.payload.source === 'task' && event.payload.source_uuid === uuid) {
            fetchNoTimelogs(uuid);
          }
        },
      },
    });

    const checklistBody = (
      <>
        <Form.Item label="Select Template" className="mb-m" style={{ position: 'relative' }}>
          <Select
            disabled={hasCheckListAnswers || (!isCreateModal && !isAssigneeEditingTheirOwnTask)}
            options={listChecklistTemplates
              .filter((item) => !item.disabled)
              .map((item) => ({ key: item.id, label: item.name, value: item.id }))}
            onChange={(value) => {
              if (hasCheckListAnswers) {
                message.warning('You cannot change the checklist because it already has answered items.');
                return;
              }

              const templateClone = listChecklistTemplates.find((item) => item.id === value);
              if (!templateClone) return;

              const newChecklist = JSON.stringify(templateClone);

              setSelectedChecklist(templateClone);
              form.setFieldsValue({ checklist: newChecklist });

              if (!isCreateModal) {
                handleChecklistUpdate(templateClone as FormBuilderData);
              }

              setChecklistStatus(getChecklistStatus(newChecklist));
            }}
            placeholder="Choose a checklist template"
            value={selectedChecklist?.id}
          />

          {selectedChecklist && (
            <div
              style={{
                position: 'absolute',
                right: 10,
                top: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Tag
                color={hasCheckListAnswers ? '' : 'blue'}
                style={{
                  padding: '0 10px',
                  fontSize: 12,
                  height: 'auto',
                  ...(hasCheckListAnswers ? { cursor: 'not-allowed', opacity: 0.5 } : { cursor: 'pointer' }),
                }}
                onClick={async () => {
                  if (hasCheckListAnswers) return;

                  form.setFieldsValue({ checklist: null });
                  setSelectedChecklist(undefined);
                  setChecklistStatus(getChecklistStatus(null));

                  if (!isCreateModal) {
                    await onUpdateHandler({ checklist: null });
                  }
                }}
              >
                Remove
              </Tag>
            </div>
          )}
        </Form.Item>

        {selectedChecklist && (
          <>
            <Checklist
              checklist={selectedChecklist}
              taskStatus={state?.status}
              onChange={handleChecklistUpdate}
              readOnly={!isCreateModal && !isAssigneeEditingTheirOwnTask}
            />
            <Form.Item name="checklist" noStyle>
              <Input hidden />
            </Form.Item>
          </>
        )}
      </>
    );

    const taskBody = (
      <>
        <Modal
          visible={openUploadingFiles}
          footer={null}
          maskClosable={false}
          onCancel={() => setOpenUploadingFiles(false)}
          destroyOnClose
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <div className="primary">
              <InfoCircleOutlined style={{ fontSize: 32 }} />
            </div>
            <div>
              <span style={{ paddingRight: 12 }}>Finishing to upload files, please wait... </span>
              <Spin indicator={<LoadingOutlined style={{ fontSize: 22 }} spin />} />
            </div>
          </div>
        </Modal>

        <Modal
          visible={openUnsavedAlert}
          className="ant-modal-confirm"
          okText="Continue creating"
          cancelText="Close it anyway"
          cancelButtonProps={{
            onClick: () => {
              setOpenUnsavedAlert(false);
              onClose?.();
            },
          }}
          onOk={() => {
            setOpenUnsavedAlert(false);
          }}
          onCancel={() => {
            setOpenUnsavedAlert(false);
          }}
          destroyOnClose
        >
          <div className="ant-modal-confirm-body">
            <ExclamationCircleFilled style={{ fontSize: 20 }} />

            <span className="ant-modal-confirm-title">Unsaved changes</span>

            <div className="ant-modal-confirm-content">
              You have unsaved changes in this task, do you really want to close it?
            </div>
          </div>
        </Modal>

        <div className="task-card">
          <div className="task-card--header">
            {state?.parent ? <ParentTask task={state} onChange={onUpdateParentTask} /> : undefined}

            {createToParentTask ? <Text className="primary">{createToParentTask.title}</Text> : undefined}

            <Form
              form={form}
              size="small"
              initialValues={initialValues}
              onFieldsChange={onFieldsChangeHandler}
              validateTrigger={['onSubmit']}
            >
              <Row style={{ paddingRight: 30 }}>
                <Col className="task-card--header-col-title">
                  <span style={{ paddingTop: 4 }}>
                    <Form.Item name="priority" noStyle>
                      <PriorityDropdown />
                    </Form.Item>
                  </span>

                  <Form.Item
                    name="title"
                    className="mb-0 w-full"
                    rules={[
                      {
                        required: true,
                        message: 'This field is required',
                      },
                    ]}
                  >
                    <EditableParagraphInput
                      ref={titleRef}
                      maxLength={72}
                      onChangeDebounce={() => onFieldsChangeHandler([], [])}
                    />
                  </Form.Item>
                </Col>

                {connectedUsers.size ? (
                  <Col xs={24} className="task-card--header-col-users sm-flex-none">
                    <UserAvatarsPopover users={Array.from(connectedUsers.values())} maxCount={2} />
                  </Col>
                ) : undefined}
              </Row>

              <div style={{ display: 'flex', flexWrap: 'nowrap' }}>
                {!isCreateModal ? (
                  <Dropdown
                    overlay={
                      <Menu>
                        <Menu.Item key="copy-task" style={{ padding: 0 }}>
                          <Link
                            to={`?task_uuid=create&task_copy_uuid=${uuid}`}
                            style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', padding: '4px 8px' }}
                          >
                            Copy
                          </Link>
                        </Menu.Item>
                        <Menu.Item key="delete-task" style={{ padding: 0 }}>
                          <Popconfirm placement="topRight" onConfirm={handleDeleteTask} title="Do you want to delete this task?">
                            <div style={{ padding: '4px 8px', width: '100%', cursor: 'pointer' }}>Delete</div>
                          </Popconfirm>
                        </Menu.Item>
                      </Menu>
                    }
                  >
                    <div
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '22px', height: '22px' }}
                    >
                      <HiDotsVertical />
                    </div>
                  </Dropdown>
                ) : undefined}

                <Form.Item
                  name="project_id"
                  style={{
                    flex: '1 1 auto',
                    overflow: 'hidden',
                    ...(!isCreateModal ? { margin: '0 0 0 6px' } : { margin: '0 0 0 28px' }),
                  }}
                  rules={[
                    {
                      required: true,
                      message: 'This field is required',
                    },
                  ]}
                >
                  <ProjectTag
                    isCreateModal={isCreateModal}
                    project={selectedProject}
                    canEdit={!state?.parent && !createToParentTask && !isPersonal && (!isCreateModal || !project)}
                    onChange={(value) => {
                      setProject(value);
                      if (isCreateModal) {
                        titleRef.current?.focus();
                      }
                    }}
                  />
                </Form.Item>

                {state?.creator ? (
                  <Tooltip title={`At ${moment(state.created_at).format('MM/DD/YY')}`} placement="left">
                    <span className="secondary" style={{ flex: 'none' }}>
                      By: {getNameAbbrev(state.creator.name)}
                    </span>
                  </Tooltip>
                ) : undefined}
              </div>
            </Form>

            {isCreateModal || state?.recurrence ? (
              <Button
                size="large"
                type="primary"
                disabled={hasFileLoading || loadingImages > 0}
                loading={saving}
                style={{ marginTop: 12 }}
                onClick={isCreateModal ? onCreateHandler : onButtonUpdateHandler}
                block
              >
                Submit
              </Button>
            ) : undefined}

            <Row gutter={8} wrap={false}>
              {state?.recurrence_type && state?.recurrence_type !== 'none' && (
                <Col>
                  <StatusTag value={state?.recurrence_type} />
                </Col>
              )}
            </Row>
          </div>

          <div className="task-card--body app-scrollbar" ref={scrollRef}>
            <div style={{ flex: '1 1 auto' }}>
              <Form
                className={`form-box-outline${selectedProject ? '' : ' read-only'}`}
                style={{
                  opacity: selectedProject ? undefined : 0.4,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  overflowX: 'hidden',
                  paddingBottom: 12,
                }}
                colon={false}
                form={form}
                size="middle"
                layout="vertical"
                initialValues={initialValues}
                onFieldsChange={onFieldsChangeHandler}
              >
                <Row gutter={[8, 8]} style={{ marginTop: 10, marginBottom: 8 }}>
                  <Col xs={24} sm={12} className="md-flex-auto">
                    <Form.Item
                      label={
                        <>
                          <TbStatusChange />
                          Status
                        </>
                      }
                      className="mb-0"
                      name="status"
                    >
                      <Select
                        listHeight={300}
                        dropdownMatchSelectWidth={130}
                        onChange={(value: any) => {
                          setState((prevState: any) => ({ ...prevState, status: value }));
                        }}
                        options={
                          isCreateModal
                            ? [getTaskStatusOption(createToStatus ?? 'todo')]
                            : state?.recurrence
                            ? [
                                {
                                  label: 'Master',
                                  value: 'seed',
                                },
                              ]
                            : getTaskStatusOptions({
                                status: state?.status,
                                responsible: state?.responsibles,
                              }).map((option) => {
                                const isCompletedStatus = option.value === 'completed';

                                let hasChecklistAtLeastOneAnswer = false;
                                let shouldBlockStatus;

                                if (selectedChecklist && selectedChecklist.form) {
                                  hasChecklistAtLeastOneAnswer = selectedChecklist.form.some(
                                    (item) => item.type === 'checkbox' && Array.isArray(item.answer) && item.answer.length > 1,
                                  );
                                }

                                if (hasCheckListAnswers) {
                                  shouldBlockStatus = true;
                                }

                                if (checklistStatus?.status === 'completed') {
                                  shouldBlockStatus = false;
                                }

                                const shouldDisable =
                                  shouldBlockStatus || (hasChecklistAtLeastOneAnswer && (noTimelogs as boolean));

                                return {
                                  ...option,
                                  disabled: isCompletedStatus
                                    ? shouldDisable
                                    : checklistStatus?.statusLocks?.includes(option.value),
                                };
                              })
                        }
                        placeholder="Status"
                      />
                    </Form.Item>
                  </Col>

                  <Col
                    flex={isPersonal ? 'auto' : undefined}
                    xs={isPersonal ? undefined : 24}
                    sm={isPersonal ? undefined : 12}
                    className={isPersonal ? undefined : 'md-flex-none'}
                  >
                    <Form.Item
                      label={
                        <>
                          <TbCalendarEvent />
                          Start date / Due date
                        </>
                      }
                      name="start_due_date"
                      style={{ marginBottom: '0' }}
                      rules={[
                        ...(state?.recurrence
                          ? [
                              {
                                required: true,
                                message: 'This field is required',
                              },
                            ]
                          : []),
                      ]}
                    >
                      <StartDueDatePicker />
                    </Form.Item>
                  </Col>

                  {!isCreateModal && !state?.recurrence && (
                    <Col
                      flex={isPersonal ? 'auto' : undefined}
                      xs={isPersonal ? undefined : 24}
                      className={isPersonal ? undefined : 'sm-flex-none'}
                      style={{ width: 140 }}
                    >
                      <Form.Item
                        label={
                          <>
                            <TbClockPlus />
                            Time tracking
                          </>
                        }
                        style={{ marginBottom: '0', width: '100%' }}
                      >
                        <Button type="ghost" style={{ width: '100%' }}>
                          <TimerDropdown
                            insideTasksDropdown
                            onAddEntry={() => {
                              dispatch(taskTimelogReportSlice.actions.fetch());
                              fetchNoTimelogs(uuid);

                              if (state?.status === 'todo') {
                                form.setFieldsValue({ status: 'in_progress' });
                                setState((prev: any) => ({ ...prev, status: 'in_progress' }));
                                onFieldsChangeHandler([], []);
                                onAddTimeLogEntry?.();
                              }
                            }}
                            needsAlert
                            taskStatus={state?.status}
                            projectId={selectedProject?.uuid}
                            key={state?.uuid}
                            task_id={state?.uuid}
                            showUserSelection
                          />
                        </Button>
                      </Form.Item>
                    </Col>
                  )}

                  {!isPersonal && (
                    <Col className="col-responsible">
                      <SelectResponsible
                        form={form}
                        state={state}
                        finalProject={selectedProject}
                        setState={setState}
                        dropdownMatchSelectWidth={190}
                        onlyAvatar
                        avatarPopover
                        avatarPopoverPlacement="leftTop"
                      />
                    </Col>
                  )}

                  <Col flex="none">
                    <Button
                      size="small"
                      type="default"
                      style={{ height: 52, padding: '0 4px' }}
                      onClick={() => setCollapsedFilters(!collapsedFilters)}
                    >
                      {collapsedFilters ? (
                        <MdKeyboardDoubleArrowUp style={{ fontSize: 20, verticalAlign: 'middle' }} />
                      ) : (
                        <MdKeyboardDoubleArrowDown style={{ fontSize: 20, verticalAlign: 'middle' }} />
                      )}
                    </Button>
                  </Col>
                </Row>

                <Collapse bordered={false} activeKey={collapsedFilters ? '1' : ''} className="headless">
                  <Collapse.Panel header="" key="1" forceRender>
                    <Row gutter={[8, 8]} style={{ marginBottom: 10 }}>
                      <Col xs={24} sm={isPersonal ? 24 : 12}>
                        <FieldTimeRangeCustom
                          name="estimated_time_range"
                          className="mb-0 w-full"
                          label={
                            <>
                              <TbClock />
                              Estimated Time
                            </>
                          }
                        />
                      </Col>

                      {!isPersonal && (
                        <Col xs={24} sm={12}>
                          <SelectFollowers form={form} state={state} finalProject={selectedProject} setState={setState} />
                        </Col>
                      )}

                      <Col xs={24} sm={!isCreateModal && !isPersonal ? 18 : 24}>
                        <TaskTagSelect />
                      </Col>

                      {!isCreateModal && !isPersonal && (
                        <Col xs={24} sm={6}>
                          <Form.Item
                            className="mb-0 form-item-horizontal"
                            label={<TbCurrencyDollar />}
                          >
                            <BillableToggle
                              value={state?.billable}
                              size={16}
                              resource="task"
                              disabled={noTimelogs !== true}
                              onChange={handleBillableChange}
                              placement="bottomLeft"
                              label={state?.billable === false ? 'Non-billable' : 'Billable'}
                              hideIcon
                              block
                            />
                          </Form.Item>
                        </Col>
                      )}

                      {isCreateModal && !isPersonal && (
                        <Col xs={24} sm={3}>
                          <Form.Item className="mb-0" name="billable" label="Billable">
                            <BillableToggle size={20} resource="task" placement="bottomLeft" block />
                          </Form.Item>
                        </Col>
                      )}

                      {(isCreateModal || state?.recurrence) && (
                        <Col xs={24} sm={4}>
                          <Form.Item className="mb-0" name="recurrence" valuePropName="checked" label="Recurrence">
                            <Switch
                              disabled={!isCreateModal}
                              onChange={(checked) => {
                                const statusVal = checked ? 'seed' : 'todo';

                                setState((prevState: any) => ({ ...prevState, recurrence: checked, status: statusVal }));

                                form.setFieldsValue({ recurrence_type: null, status: statusVal });
                              }}
                              checkedChildren="ON"
                              unCheckedChildren="OFF"
                            />
                          </Form.Item>
                        </Col>
                      )}

                      {state?.recurrence && (
                        <>
                          <Col xs={12} sm={8}>
                            <Form.Item
                              label="Recurrence type"
                              name="recurrence_type"
                              className="mb-0"
                              rules={[
                                {
                                  required: true,
                                  message: 'This field is required',
                                },
                              ]}
                            >
                              <Select
                                options={['weekly', 'bi_weekly', 'monthly', 'every_3_months', 'every_6_months', 'yearly'].map(
                                  (item) => ({
                                    label: convertSnakeCaseToTitleCase(item),
                                    value: item,
                                  }),
                                )}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={12} sm={9}>
                            <Form.Item
                              label="Recurrence limit"
                              name="recurrence_limit"
                              className="mb-0"
                              rules={[
                                {
                                  required: true,
                                  message: 'This field is required',
                                },
                              ]}
                            >
                              <InputNumber style={{ width: '100%' }} min={0} />
                            </Form.Item>
                          </Col>
                        </>
                      )}
                    </Row>
                  </Collapse.Panel>
                </Collapse>

                <Tabs
                  className="task-tabs"
                  defaultActiveKey="description"
                  style={{
                    flex: '1 1 auto',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                  }}
                >
                  <Tabs.TabPane tab="Description" key="description" style={{ height: '100%' }}>
                    <RichTextEditor
                      ref={descriptionTextRef}
                      scrollingContainer={scrollRef.current ?? undefined}
                      wrapperClassName="flex-auto editor-full-height"
                      placeholder={descriptionPlaceholder}
                      initialValue={state?.description_delta}
                      showMoreButton={!isCreateModal}
                      progressBar="bottom"
                      imageAsData64
                      containerFullHeight
                      onImageModalChanged={onImageModalChanged}
                      onLoadingImage={setLoadingImages}
                      onImageUploader={async (upload) => {
                        setFilesData((prev) => [...prev, upload]);

                        if (!isCreateModal) {
                          await onUpdateHandler({ uploads: [upload.response?.uuid] });
                        }

                        return undefined;
                      }}
                      collaborativeEditor={
                        !isCreateModal
                          ? {
                              room: `task-editor-${uuid}`,
                              users: connectedUsers,
                              status: editorStatus,
                              onUserUpdated: setConnectedUsers,
                              onStatusChanged: setEditorStatus,
                              onYDocUpdated: async (description) => {
                                await apiRequests.put(`${apiRoutes.PROJECT_TASKS}/${uuid}/description-delta`, {
                                  description_delta: description,
                                });
                              },
                            }
                          : { useYDoc: true }
                      }
                    />
                  </Tabs.TabPane>

                  <Tabs.TabPane tab={checklistStatusRender} key="checklist">
                    {checklistBody}
                  </Tabs.TabPane>
                </Tabs>
              </Form>
            </div>

            <div>
              <Collapse className="task-modal-collapse no-padding mb-m">
                <Panel header="Assets" key="asset" extra={<Badge count={filesData.length} showZero />}>
                  <UploadDragger
                    type="picture"
                    uploads={filesData}
                    setLoading={setHasFileLoading}
                    setUploads={setFilesData}
                    showDownloadIcon
                    showRemoveIcon
                    resource={!isCreateModal ? 'task' : undefined}
                    resourceIds={[uuid]}
                    onRequest={
                      isCreateModal
                        ? undefined
                        : async ({ uuid: uploadUuid }) => {
                            return !!(await onUpdateHandler({ uploads: [uploadUuid] }));
                          }
                    }
                  />
                </Panel>
              </Collapse>

              {!isCreateModal && !state?.recurrence && (
                <SubTasksCollapse
                  task={state}
                  onTaskUpdate={async () => {
                    onUpdateSubtask?.();
                    await fetchTask();
                  }}
                />
              )}

              {!isCreateModal && (
                <RequestCard
                  taskId={uuid}
                  projectUuid={selectedProject?.uuid}
                  requests={state?.requests ?? []}
                  onRelate={(newRequests) => {
                    setState((oldState: any) => ({
                      ...oldState,
                      requests: newRequests,
                    }));
                  }}
                  onRemove={(request) => {
                    setState((oldState: any) => ({
                      ...oldState,
                      requests: oldState.requests.filter((x: any) => x.uuid !== request.uuid),
                    }));
                    onRemoveRequest?.(state.uuid);
                  }}
                />
              )}

              {!isCreateModal && (
                <Tabs>
                  {!hideComments && (
                    <Tabs.TabPane tab="Comments" key="item-1">
                      <Comments
                        key={state?.uuid}
                        type="tasks"
                        typeUuid={state?.uuid}
                        highlightUuid={highlightCommentUuid}
                        projectUuid={selectedProject?.uuid}
                        isProjectPersonal={selectedProject?.kind === 'personal'}
                        textEditorClassName="max-h"
                        maxHeight="370px"
                        scrollingContainer=".task-card--body"
                        mentionTypes={['regular_user', 'freelancer', 'group']}
                        hideTemplates
                        disableSearch
                        onCommentsLoaded={() => {
                          const timer = setTimeout(() => {
                            scrollToBottom();
                            if (onCommentsLoaded) {
                              onCommentsLoaded();
                            }
                          }, 100);

                          return () => {
                            clearTimeout(timer);
                          };
                        }}
                        onFinishSubmit={(_, images) => {
                          setFilesData((old) => [...old, ...images]);
                        }}
                        onFilesDelete={(listUuid) => {
                          setFilesData((old) => old.filter((item) => !listUuid.includes(item.response?.uuid ?? item.uid)));
                        }}
                        onImageModalChanged={onImageModalChanged}
                      />
                    </Tabs.TabPane>
                  )}

                  <Tabs.TabPane tab="Task activity" key="item-2">
                    <ActivityLogs model={state} modelType="task" />
                  </Tabs.TabPane>

                  <Tabs.TabPane tab="Timelogs" key="item-3">
                    <TaskTimelogTable uuid={uuid} />
                  </Tabs.TabPane>
                </Tabs>
              )}
            </div>
          </div>
        </div>
      </>
    );

    if (fetchTaskError) {
      return <ErrorResult error={fetchTaskError} />;
    }

    return fetching || fetchingParent ? <Skeleton active /> : taskBody;
  },
);

TaskModalBody.displayName = 'TaskModalBody';

interface TaskModalProps {
  visible: any;
  highlightCommentUuid?: string;
  createToParentUuid?: string;
  createToStatus?: string;
  createFromTaskUuid?: string;
  project?: any;
  initialFiles?: UploadFileApi[];
  isPersonal?: boolean;
  onClose?: () => void;
  onCommentsLoaded?: any;
  onAddTimeLogEntry?: any;
  onRemoveRequest?: (taskId: string) => void;
  onUpdateSubtask?: () => void;
  onDeleteTask?: () => void;
  onCustomizeDataApi?: (values: Record<string, any>) => Record<string, any>;
  onSaved?: (data: Record<string, any>) => void;
}

const TaskModal: FC<TaskModalProps> = ({
  visible,
  highlightCommentUuid,
  createToParentUuid,
  createToStatus,
  createFromTaskUuid,
  project,
  initialFiles,
  isPersonal,
  onClose,
  onCommentsLoaded,
  onAddTimeLogEntry,
  onRemoveRequest,
  onUpdateSubtask,
  onSaved,
  onCustomizeDataApi,
  onDeleteTask,
}) => {
  const taskBodyRef = useRef<TaskModalBodyHandle>(null);
  const uuidRef = useRef(visible);
  const [enableDrawerKeyboard, setEnableDrawerKeyboard] = useState(true);
  const [isExpanded, setIsExpanded] = useStickyState(false, '@ls-admin/drawer-size');
  const [windowWidth, setWindowWidth] = useState(() => window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isOpen = !!visible;
  const canExpand = windowWidth >= 1294;
  const drawerWidth = canExpand && isExpanded ? 1280 : 640;

  if (uuidRef.current !== visible && visible) {
    uuidRef.current = visible;
  }

  const documentClickHandler = (event: Event) => {
    let close = true;

    document.querySelectorAll('.ignore-close-drawer').forEach((item) => {
      if (item === event.target || (event.target && item.contains(event.target as any))) {
        close = false;
      }
    });

    if (close) {
      taskBodyRef.current?.safeClose(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.querySelector('#root')?.addEventListener('click', documentClickHandler);
    } else {
      document.querySelector('#root')?.removeEventListener('click', documentClickHandler);
    }

    return () => {
      document.querySelector('#root')?.removeEventListener('click', documentClickHandler);
    };
  }, [isOpen]);

  return (
    <Drawer
      visible={isOpen}
      mask={uuidRef.current === 'create' ? undefined : false}
      maskStyle={uuidRef.current === 'create' ? { background: 'transparent' } : undefined}
      width={drawerWidth}
      footer={null}
      keyboard={enableDrawerKeyboard}
      className="task-drawer"
      bodyStyle={{
        padding: 0,
      }}
      destroyOnClose
      maskClosable
      onClose={() => {
        taskBodyRef.current?.safeClose(false);
      }}
    >
      <div style={{ position: 'relative', height: '100%' }}>
        {canExpand && isOpen && (
          <div
            className="task-drawer-edge-handle"
            style={{ right: drawerWidth - 12 }}
            onClick={() => setIsExpanded((prev: boolean) => !prev)}
          >
            {isExpanded ? <DoubleRightOutlined /> : <DoubleLeftOutlined />}
          </div>
        )}
        <div style={{ padding: '16px 16px 0 16px', height: '100%' }}>
        <TaskModalBody
          key={uuidRef.current}
          uuid={uuidRef.current}
          ref={taskBodyRef}
          highlightCommentUuid={highlightCommentUuid}
          createToParentUuid={createToParentUuid}
          createToStatus={createToStatus}
          createFromTaskUuid={createFromTaskUuid}
          project={project}
          initialFiles={initialFiles}
          isPersonal={isPersonal}
          onClose={onClose}
          onCommentsLoaded={onCommentsLoaded}
          onAddTimeLogEntry={onAddTimeLogEntry}
          onRemoveRequest={onRemoveRequest}
          onUpdateSubtask={onUpdateSubtask}
          onDeleteTask={onDeleteTask}
          onImageModalChanged={(open) => setEnableDrawerKeyboard(!open)}
          onSaved={onSaved}
          onCustomizeDataApi={onCustomizeDataApi}
        />
        </div>
      </div>
    </Drawer>
  );
};

export { TaskModalBody };

export default TaskModal;
